import { selectUserProjectTimeZone } from 'core/userContext';
import isEmpty from 'lodash/isEmpty';
import { Dispatch, SetStateAction } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { InfoBanner } from 'ui/banner';
import { Link } from 'ui/links';
import { body2, body2Bold, colors, overline, spacing } from 'ui/theme';

import { CampaignAlert } from '../../hooks/useCampaignsTriggeredAlerts';
import { AlertHistoryCard, AlertHistoryList } from './AlertHistory.styled';
import { AlertHistoryDetails } from './AlertHistoryDetails';
import { AlertHistoryItem } from './AlertHistoryItem';
import {
  getAlertHistoryItemCaptionDisplayText,
  getAlertHistoryItemLabelDisplayText,
} from './AlertsHistorySlideOver.utils';

const HistoryTitle = styled.div`
  ${overline}
  color: ${colors.textSecondary};
  margin-bottom: ${spacing.threeQuarter};
`;

const AlertHistoriesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.double};
`;

const AlertHistoryItemLabel = styled.span`
  ${body2Bold}
  color: ${colors.textPrimary};
`;

const AlertHistoryItemSubLabel = styled.span`
  ${body2}
  color: ${colors.textSecondary};
`;

const AlertHistorySection = ({
  title,
  alerts,
  id,
  setSelectedAlert,
}: {
  alerts: CampaignAlert[];
  title: string;
  id: string;
  setSelectedAlert: (alert: CampaignAlert) => void;
}) => (
  <div id={id} data-test={id}>
    <HistoryTitle>{title}</HistoryTitle>
    <AlertHistoryCard withPadding={false}>
      <AlertHistoryList>
        {alerts?.map((alert) => {
          const { frequencyText, thresholdText } = getAlertHistoryItemLabelDisplayText(
            alert,
            false,
          );

          const projectTimeZone = useSelector(selectUserProjectTimeZone);
          const caption = getAlertHistoryItemCaptionDisplayText(
            alert.triggeredTime,
            projectTimeZone,
          );

          return (
            <AlertHistoryItem
              key={alert?.id}
              data-test={`alert-history-item-${alert?.id}`}
              label={
                <>
                  <AlertHistoryItemLabel>{thresholdText}</AlertHistoryItemLabel>{' '}
                  <AlertHistoryItemSubLabel>{frequencyText}</AlertHistoryItemSubLabel>
                </>
              }
              caption={caption}
              setSelectedAlert={() => setSelectedAlert(alert)}
            />
          );
        })}
      </AlertHistoryList>
    </AlertHistoryCard>
  </div>
);

const StyledInfoBanner = styled(InfoBanner)`
  margin-bottom: ${spacing.threeQuarter};
`;

type AlertHistoriesProps = {
  campaignId: number;
  campaignAlertsEnabled?: boolean;
  recentAlerts: CampaignAlert[];
  pastAlerts: CampaignAlert[];
  selectedAlert: CampaignAlert | null;
  setSelectedAlert: Dispatch<SetStateAction<CampaignAlert | null>>;
};

export const AlertHistories = ({
  setSelectedAlert,
  selectedAlert,
  pastAlerts,
  recentAlerts,
  campaignId,
  campaignAlertsEnabled,
}: AlertHistoriesProps) => {
  const hasRecentAlerts = recentAlerts?.length > 0;
  const hasPastAlerts = pastAlerts?.length > 0;

  return !isEmpty(selectedAlert) ? (
    <AlertHistoryDetails
      alert={selectedAlert as CampaignAlert}
      onBack={() => setSelectedAlert(null)}
    />
  ) : (
    (hasRecentAlerts || hasPastAlerts) && (
      <>
        {!campaignAlertsEnabled && (
          <StyledInfoBanner>
            Alerts are disabled for this campaign. You can enable alerts in the{' '}
            <Link href={`/campaigns/${campaignId}?view=Edit`}>alert settings</Link>.
          </StyledInfoBanner>
        )}
        <AlertHistoriesWrapper data-test="alert-histories-wrapper">
          {hasRecentAlerts && (
            <AlertHistorySection
              title="Past 24 hours"
              alerts={recentAlerts}
              id="recent-alerts"
              setSelectedAlert={setSelectedAlert}
            />
          )}
          {hasPastAlerts && (
            <AlertHistorySection
              title="Past Alerts"
              alerts={pastAlerts}
              id="past-alerts"
              setSelectedAlert={setSelectedAlert}
            />
          )}
        </AlertHistoriesWrapper>
      </>
    )
  );
};
